import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { AudioLines, Mic, Paperclip, Plus } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useAgentName } from '../../../lib/use-agent-name';
import { STICKY_CHAT_PAGE_INSET_PX } from '../../../lib/sticky-chat-routes';
import { useMeasuredHeight } from '../../../lib/use-measured-height';
import { HomeComposer } from '../HomeComposer';
import { useKeyboardInset } from '../use-keyboard-inset';
import { StickyCreateMenu } from './CreateMenu';
import styles from './StickyChatBar.module.css';

// Figma 21882:142190.
const OPEN_BAND_PX = 178;

/**
 * The sticky "Ask {maestro}" bar — the chat entry that replaces the round FAB under the activity-map home
 * spec (`stickyChatPillOn`). Web twin of the phones' resting pill (iOS `ChatComposerFAB` +
 * `RestingComposerPill`, Android `ChatComposerFab(restingPill)`), and it keeps their logic:
 *
 *  - At rest the pill is one tap target. Its glyphs are a preview, so the two states cannot behave
 *    differently.
 *  - A tap opens the input-only composer in the same place, over an outside-tap catcher. Sending hands
 *    off to a FRESH chat (`HomeComposer.submit`), the rule both phones run for this composer.
 *  - It always reads "Ask {maestro}". A page keeps its own "talk to Maestro" button; the bar never carries it.
 *  - On a phone the resting pill has a "+" beside it that opens the create menu (`StickyCreateMenu`).
 *
 * While it is mounted it publishes `--sticky-chat-inset`, which the content pane spends as an end-of-scroll
 * spacer (globals.css), so a page's last row can scroll clear of the bar — the phones' `StickyChatPage`
 * inset.
 */
export function StickyChatBar({ narrow, hidden = false }: { narrow: boolean; hidden?: boolean }) {
  const { t } = useTranslation();
  const agent = useAgentName();
  const { logPageClickBi } = useGenericBi();
  const keyboardInset = useKeyboardInset();
  const [open, setOpen] = useState(false);

  // A route change closes the composer.
  const { asPath } = useRouter();
  useEffect(() => {
    setOpen(false);
  }, [asPath]);
  const placeholder = t('home.activityMap.askMaestro', { maestroAgentName: agent.short });

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--sticky-chat-inset', `${STICKY_CHAT_PAGE_INSET_PX}px`);
    return () => {
      root.style.removeProperty('--sticky-chat-inset');
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const slot = `${styles.slot} ${narrow ? styles.slotNarrow : styles.slotWide}${hidden ? ` ${styles.slotHidden}` : ''}`;

  // The gradient + backdrop-blur scrim behind the bar — content dissolves into the page and blurs as it
  // scrolls under it. The resting pill has it (Figma 21639:675107), and so does the open composer on wide,
  // in a taller band to cover the taller composer (Figma 21882:142190). The open composer on a phone has its
  // dimmed outside-tap scrim instead. Hidden while the drawer hides the bar.
  const renderBand = (openGrowthPx?: number) => hidden || (open && narrow) ? null : (
    <div
      aria-hidden
      className={`${styles.scrimBand} ${narrow ? styles.scrimBandNarrow : styles.scrimBandWide}${open ? ` ${styles.scrimBandOpen}` : ''}`}
      style={openGrowthPx ? { blockSize: OPEN_BAND_PX + openGrowthPx } : undefined}
    >
      {/* Progressive backdrop blur 0→4px, then the page-surface tint over it (frosted glass). */}
      <div className={`${styles.blurLayer} ${styles.blur1}`} />
      <div className={`${styles.blurLayer} ${styles.blur2}`} />
      <div className={`${styles.blurLayer} ${styles.blur3}`} />
      <div className={`${styles.blurLayer} ${styles.blur4}`} />
      <div className={styles.scrimTint} />
    </div>
  );
  const band = renderBand();

  const expand = () => {
    // The phones' event for this tap (`SymphonyBI.clickCta("expand_composer")`).
    logPageClickBi({ page_name: 'chat', button_name: 'expand_composer' });
    setOpen(true);
  };

  if (!open && narrow) {
    // Figma 21639:656690: a glass pill (attach, the placeholder, mic, voice) and the "+" beside it.
    return (
      <>
      {band}
      <div className={`${slot} ${styles.slotWithCreate}`}>
        <button type="button" className={`${styles.pill} ${styles.pillNarrow}`} aria-label={placeholder} onClick={expand}>
          <span className={styles.glyph} aria-hidden>
            <Paperclip size={16} strokeWidth={1.5} absoluteStrokeWidth />
          </span>
          <span className={`${styles.placeholder} sym-type-medium-regular`}>{placeholder}</span>
          <span className={styles.glyph} aria-hidden>
            <Mic size={16} strokeWidth={1.5} absoluteStrokeWidth />
          </span>
          <span className={styles.glyph} aria-hidden>
            <AudioLines size={16} strokeWidth={1.5} absoluteStrokeWidth />
          </span>
        </button>
      </div>
      <StickyCreateMenu pageName="chat" hidden={hidden} />
      </>
    );
  }

  if (!open) {
    return (
      <>
      {band}
      <div className={slot}>
        <button type="button" className={styles.pill} aria-label={placeholder} onClick={expand}>
          <span className={styles.glyph} aria-hidden>
            <Plus size={16} strokeWidth={1.5} absoluteStrokeWidth />
          </span>
          <span className={`${styles.placeholder} sym-type-small-regular`}>{placeholder}</span>
          <span className={styles.glyph} aria-hidden>
            <Mic size={16} strokeWidth={1.5} absoluteStrokeWidth />
          </span>
          <span className={`${styles.speak} sym-type-small-semi-bold`} aria-hidden>
            <AudioLines size={16} strokeWidth={1.5} absoluteStrokeWidth />
            {t('home.composer.speak')}
          </span>
        </button>
      </div>
      </>
    );
  }

  return (
    <OpenComposer
      renderBand={renderBand}
      scrim={
        <div
          className={`${styles.scrim} ${narrow ? styles.scrimNarrow : ''}`}
          aria-hidden
          onClick={() => {
            logPageClickBi({ page_name: 'chat', button_name: 'dismiss_chat' });
            setOpen(false);
          }}
        />
      }
      slotClassName={`${slot} ${styles.slotExpanded}`}
      // The narrow composer rides the on-screen keyboard, as the round FAB's peek composer did.
      slotStyle={narrow && keyboardInset ? { bottom: keyboardInset + 8 } : undefined}
    >
      <HomeComposer
        maxWidth="none"
        autoFocus
        placeholder={placeholder}
        onSubmitted={() => setOpen(false)}
      />
    </OpenComposer>
  );
}

/**
 * Mounts with each open, so its measurements start fresh. The band keeps its design height for the
 * composer as it opens and grows only by what the composer gains after that (a multi-line draft, staged
 * chips), so the composer's top never rises above the band.
 */
function OpenComposer({
  renderBand,
  scrim,
  slotClassName,
  slotStyle,
  children,
}: {
  renderBand: (growthPx?: number) => ReactNode;
  scrim: ReactNode;
  slotClassName: string;
  slotStyle?: CSSProperties;
  children: ReactNode;
}) {
  const [composerRef, composerHeight] = useMeasuredHeight();
  const [baseline, setBaseline] = useState<number | null>(null);
  useEffect(() => {
    if (baseline === null && composerHeight !== null) setBaseline(composerHeight);
  }, [baseline, composerHeight]);
  const growth = baseline !== null && composerHeight !== null ? Math.max(0, composerHeight - baseline) : 0;

  return (
    <>
      {renderBand(growth)}
      {scrim}
      <div className={slotClassName} style={slotStyle}>
        <div ref={composerRef} className={styles.composer}>
          {children}
        </div>
      </div>
    </>
  );
}
